import { updatePathShallow } from "@/utils/updatePathShallow"
import { usePathname, useSearchParams } from "next/navigation"
import { useCallback, useMemo } from "react"
import { create } from "zustand"

type FilterStore = {
  currentOpenMenu: string | null
  toggleOpenMenu: (f: string) => void
  setCloseMenu: () => void
}

type NameData = {
  id: string
  name: string
}

const useFilterStore = create<FilterStore>((set, get) => ({
  currentOpenMenu: null,
  toggleOpenMenu: (key: string) => {
    const openMenu = get().currentOpenMenu
    set(() => ({ currentOpenMenu: openMenu === key ? null : key }))
  },
  setCloseMenu: () => {
    set(() => ({ currentOpenMenu: null }))
  },
}))

export const useFilters = () => {
  const searchParams = useSearchParams()
  const { currentOpenMenu, toggleOpenMenu, setCloseMenu } = useFilterStore()

  const getButtonText = ({
    defaultText,
    data,
    selectedIds,
    showCount = true,
  }: {
    defaultText: string
    data: (NameData | null)[]
    selectedIds: string[]
    showCount?: boolean
  }) => {
    if (!!data && data?.length > 0 && selectedIds && selectedIds.length > 0) {
      const firstFilterName = data?.find(role => role?.id === selectedIds?.[0])?.name
      const additionalFilterText = selectedIds.length > 1 ? ` +${selectedIds.length - 1}` : ""
      if (firstFilterName) {
        return `${firstFilterName}${showCount ? additionalFilterText : ""}`
      }
    }
    return defaultText
  }

  const currentParams = useMemo(() => {
    const params: { [key: string]: string } = {}
    searchParams?.forEach((value, key) => {
      params[key] = value
    })
    return params
  }, [searchParams])

  const getActiveFilters = useCallback(
    (id: string) => {
      return currentParams[id] ? currentParams[id].split(",") : []
    },
    [currentParams]
  )

  // Remove all filters by param ids (e.g. position, skills, etc.)
  const removeAllByIds = useCallback(
    (ids: string[]) => {
      const current = searchParams ? new URLSearchParams(Array.from(searchParams.entries())) : new URLSearchParams()
      ids.forEach(id => current.delete(id))
      const query = current.toString() ? current.toString() : ""
      updatePathShallow(query)
    },
    [searchParams]
  )

  const removeAllFilters = useCallback(() => {
    updatePathShallow("")
  }, [])

  const toggleFilter = ({
    params,
    multiSelect = true,
  }: {
    params: { key: string; values: string[] }[]
    multiSelect?: boolean
  }) => {
    const current = searchParams ? new URLSearchParams(Array.from(searchParams.entries())) : new URLSearchParams()

    params.forEach(({ key, values }) => {
      if (multiSelect) {
        const existingFilters = current.get(key)
        const currentFilterIds = existingFilters ? existingFilters.split(",") : []

        values.forEach(filterId => {
          if (currentFilterIds.includes(filterId)) {
            // Remove filterId if it already exists
            const newFilterIds = currentFilterIds.filter(id => id !== filterId)
            if (newFilterIds.length > 0) {
              current.set(key, newFilterIds.join(","))
            } else {
              current.delete(key)
            }
          } else {
            // Add filterId if it doesn't exist
            currentFilterIds.push(filterId)
            current.set(key, currentFilterIds.join(","))
          }
        })
      } else {
        // If multiSelect is false, reset to just the new values
        current.set(key, values.join(","))
        toggleOpenMenu(key)
      }
    })

    const query = current.toString() ? current.toString() : ""
    updatePathShallow(query)
  }

  const onCloseMenu = useCallback(() => {
    setCloseMenu()
  }, [setCloseMenu])

  const onToggleMenu = useCallback(
    (key: string) => {
      toggleOpenMenu(key)
    },
    [toggleOpenMenu]
  )

  const activeFilters = {}

  return {
    activeFilters,
    currentOpenMenu,
    removeAllByIds,
    removeAllFilters,
    onToggleMenu,
    onCloseMenu,
    toggleFilter,
    getActiveFilters,
    getButtonText,
  }
}
